import { useState, useEffect } from 'react';
import type { WGraphiteRelatedLinkView } from '@zola/svc-web-api-ts-client';
import { getGraphiteRelatedLinks } from '@zola-helpers/client/dist/es/api/graphiteRelatedLinks';

export enum GraphiteLinkType {
  EXPERT_ADVICE = 'expert-advice',
  PREAUTH_TO_EXPERT_ADVICE = 'preauth-to-expert-advice',
  PAPER_PRODUCT = 'paper-product',
  WEDDING_TEMPLATE = 'wedding-template',

  /**
   * Links to vendor pages meant to be used on other vendor pages.
   *
   * To be used with pages with the following pattern
   * - /wedding-vendors/search/{vendor slug}
   *
   * Examples:
   * - https://www.zola.com/wedding-vendors/search/madison-wi--wedding-bands-djs
   * - https://www.zola.com/wedding-vendors/search/san-francisco-ca--wedding-venues
   * - https://www.zola.com/wedding-vendors/search/concord-ca--wedding-bands-djs
   */
  VENDOR = 'vendor',

  /**
   * Links to other vendor pages in the same location.
   *
   * To be used with pages with the following pattern
   * - /wedding-vendors/search/{vendor slug}
   *
   * Examples:
   * - https://www.zola.com/wedding-vendors/search/madison-wi--wedding-bands-djs
   * - https://www.zola.com/wedding-vendors/search/san-francisco-ca--wedding-venues
   * - https://www.zola.com/wedding-vendors/search/concord-ca--wedding-bands-djs
   */
  VENDORS_IN_SAME_CITY = 'vendor-same-city',

  /**
   * Links to other vendor pages in a nearby location.
   *
   * To be used with pages with the following pattern
   * - /wedding-vendors/search/{vendor slug}
   *
   * Examples:
   * - https://www.zola.com/wedding-vendors/search/madison-wi--wedding-bands-djs
   * - https://www.zola.com/wedding-vendors/search/san-francisco-ca--wedding-venues
   * - https://www.zola.com/wedding-vendors/search/concord-ca--wedding-bands-djs
   */
  VENDORS_IN_NEARBY_CITIES = 'vendor-nearby-city',
}

/**
 * Get related links for a URL from Graphite's internal linking API
 *
 * @param type The type of links to return, which corresponds to the unique part of the internal linking API endpoint, such as 'preauth-to-expert-advice'
 * @param path The path to get related links for, such as /wedding-vendors or /wedding-registry.  If no path is provided, no API call is made
 * @returns A list of related links for the specified path
 */
const useGraphiteRelatedLinks = (
  type: GraphiteLinkType,
  path: string | undefined | null
): WGraphiteRelatedLinkView[] => {
  const [relatedLinks, setRelatedLinks] = useState<WGraphiteRelatedLinkView[]>();

  useEffect(() => {
    if (path) {
      // Graphite's internal linking API is always based on production URLs
      const url = `https://www.zola.com${path}`;
      getGraphiteRelatedLinks(type, url)
        .then((data) => {
          setRelatedLinks(data);
        })
        .catch(() => {
          setRelatedLinks([]);
        });
    }
  }, [type, path]);

  return relatedLinks || [];
};

export default useGraphiteRelatedLinks;
